/**
 * @file VoidOrderModal.tsx
 * @description Modal dialog enforcing reason selection and confirmation for voiding/cancelling entire table orders.
 */

import React, { useState } from 'react';
import { X, AlertTriangle, Trash2 } from 'lucide-react';

interface VoidOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderNumber?: string | null;
  tableNumber: string;
  onConfirmVoid: (reason: string) => Promise<void>;
}

const VOID_REASONS = [
  'Guest walked out / left',
  'Test / training order',
  'Entered by mistake / duplicate',
  'Stale shift unfulfilled',
  'Customer changed mind',
];

export const VoidOrderModal: React.FC<VoidOrderModalProps> = ({
  isOpen,
  onClose,
  orderNumber,
  tableNumber,
  onConfirmVoid,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>(VOID_REASONS[0]);
  const [customReason, setCustomReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    const finalReason = customReason.trim() ? customReason.trim() : selectedReason;
    try {
      setIsSubmitting(true);
      await onConfirmVoid(finalReason);
      onClose();
    } catch (err) {
      console.error('[VoidOrderModal] Void order error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 shadow-2xl max-h-[92dvh] sm:max-h-[90vh] flex flex-col overflow-hidden pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:pb-6">
        {/* Mobile slide-up handle */}
        <div className="sm:hidden flex justify-center pb-2 -mt-1">
          <div className="w-10 h-1 rounded-full bg-zinc-700/60" />
        </div>

        {/* Modal Header */}
        <div className="flex items-center justify-between mb-3 shrink-0">
          <div className="flex items-center gap-2 text-rose-500">
            <AlertTriangle className="w-5 h-5 shrink-0" />
            <h3 className="text-base font-black uppercase text-white tracking-tight">Void / Cancel Order</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content Body */}
        <div className="flex-1 overflow-y-auto space-y-4 pr-0.5">
          <p className="text-xs text-zinc-400 leading-relaxed">
            Are you sure you want to void order{' '}
            <span className="font-bold text-white">{orderNumber ? `${orderNumber} ` : ''}</span>
            for <span className="font-bold text-white">Table {tableNumber}</span>? This action will mark
            the order as cancelled and clear any open kitchen items.
          </p>

          <div className="space-y-2">
            {VOID_REASONS.map(reason => (
              <button
                key={reason}
                type="button"
                onClick={() => {
                  setSelectedReason(reason);
                  setCustomReason('');
                }}
                className={`w-full min-h-[44px] p-3 rounded-xl text-xs font-semibold text-left border transition-all cursor-pointer flex items-center ${
                  selectedReason === reason && !customReason
                    ? 'bg-rose-950/40 text-rose-300 border-rose-600/80 font-bold'
                    : 'bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700'
                }`}
              >
                {reason}
              </button>
            ))}
          </div>

          <div>
            <input
              type="text"
              placeholder="Or enter custom reason..."
              value={customReason}
              onChange={e => setCustomReason(e.target.value)}
              className="w-full min-h-[44px] px-3.5 py-2.5 rounded-xl bg-zinc-900 border border-zinc-800 text-base sm:text-xs text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-rose-500"
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-3 pt-4 border-t border-zinc-800/80 mt-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 min-h-[48px] py-3 rounded-2xl bg-zinc-900 hover:bg-zinc-800 active:scale-[0.98] text-zinc-300 font-bold text-xs uppercase cursor-pointer transition-all"
          >
            Keep Order
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="flex-1 min-h-[48px] py-3 rounded-2xl bg-rose-600 hover:bg-rose-500 active:scale-[0.98] text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-lg shadow-rose-950/40 cursor-pointer disabled:opacity-50 transition-all"
          >
            <Trash2 className="w-4 h-4 shrink-0" />
            <span>{isSubmitting ? 'Voiding...' : 'Void Order'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
